import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import SchemaOrg from '../../components/feature/SchemaOrg';
import PriceSimulator from '../../components/feature/PriceSimulator';
import { useEffect } from 'react';

export default function ChoisirTransporteurParisGuidePage() {
  useEffect(() => {
    document.title = 'Comment Choisir son Transporteur à Paris | Guide Complet 2024';
    
    const metaTags = [
      { name: 'description', content: 'Guide complet pour choisir le meilleur transporteur à Paris. Critères de sélection, comparatif des services, conseils d\'experts et checklist pour faire le bon choix de coursier professionnel.' },
      { name: 'keywords', content: 'choisir transporteur Paris, comparatif transporteur, meilleur transporteur Paris, sélection coursier, guide transport Paris' },
      { name: 'geo.region', content: 'FR-75' },
      { name: 'geo.placename', content: 'Paris' },
      { name: 'geo.position', content: '48.8566;2.3522' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'Comment Choisir son Transporteur à Paris | Guide Complet 2024' },
      { name: 'twitter:description', content: 'Guide complet pour choisir le meilleur transporteur à Paris. Critères de sélection, comparatif et conseils d\'experts.' }
    ];

    metaTags.forEach(({ name, content }) => {
      let meta = document.querySelector(`meta[name="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    });

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = 'https://transporteurexpressidf.fr/blog/choisir-transporteur-paris-guide';
  }, []);

  const relatedArticles = [
    {
      slug: 'livraison-dernier-kilometre-paris',
      title: 'Livraison dernier kilomètre à Paris : enjeux et solutions',
      image: 'https://public.readdy.ai/ai/img_res/d66cd7a726c20cc646e5285b2380e332.jpg'
    },
    {
      slug: 'transport-urgent-weekend-paris',
      title: 'Transport urgent le week-end à Paris : comment ça marche ?',
      image: 'https://readdy.ai/api/search-image?query=weekend%20urgent%20delivery%20service%20in%20Paris%20professional%20courier%20working%20saturday%20sunday%20emergency%20transport%20fast%20logistics%20modern%20urban%20setting&width=400&height=250&seq=related8&orientation=landscape'
    },
    {
      slug: 'demenagement-conseils-paris',
      title: '10 conseils pour un déménagement réussi à Paris',
      image: 'https://readdy.ai/api/search-image?query=professional%20moving%20service%20in%20Paris%20apartment%20with%20boxes%20furniture%20transport%20organized%20relocation%20team%20efficient%20packing%20clean%20modern%20interior%20successful%20move&width=400&height=250&seq=related9&orientation=landscape'
    }
  ];

  return (
    <>
      <SchemaOrg 
        type="article" 
        data={{
          headline: 'Comment Choisir son Transporteur à Paris : Guide Complet',
          datePublished: '2024-01-25',
          dateModified: '2024-01-25',
          author: 'Speed Transport Paris',
          description: 'Guide complet pour choisir le meilleur transporteur à Paris. Découvrez les critères essentiels, le comparatif des services et nos conseils d\'experts pour faire le bon choix.'
        }}
      />
      <Navbar />
      <WhatsAppButton phoneNumber="0631012910" />

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-teal-500 to-emerald-600 text-white py-16">
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
          <div className="container mx-auto px-4 relative z-10">
            <nav className="mb-6">
              <ol className="flex items-center space-x-2 text-sm text-white/80">
                <li><Link to="/" className="hover:text-white transition-colors">Accueil</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li className="text-white font-medium">Choisir son Transporteur</li>
              </ol>
            </nav>
            <div className="max-w-4xl">
              <span className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm text-white text-sm font-medium rounded-full mb-4">
                Guide Expert
              </span>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Comment Choisir son Transporteur à Paris : Guide Complet 2024
              </h1>
              <div className="flex items-center space-x-6 text-white/90">
                <span className="flex items-center">
                  <i className="ri-calendar-line mr-2"></i>
                  25 janvier 2024
                </span>
                <span className="flex items-center">
                  <i className="ri-time-line mr-2"></i>
                  10 min de lecture
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Image */}
        <section className="container mx-auto px-4 -mt-12 relative z-10">
          <div className="max-w-5xl mx-auto">
            <div className="relative w-full h-96 rounded-xl overflow-hidden shadow-2xl">
              <img
                src="https://readdy.ai/api/search-image?query=professional%20transport%20company%20selection%20guide%20Paris%20logistics%20comparison%20checklist%20business%20decision%20making%20modern%20office%20setting%20professional%20meeting%20discussing%20delivery%20options%20clean%20background&width=1200&height=600&seq=hero-guide&orientation=landscape"
                alt="Comment choisir son transporteur à Paris - Guide complet"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto">
            {/* Introduction */}
            <div className="prose prose-lg max-w-none mb-12">
              <p className="text-xl text-gray-700 leading-relaxed mb-6">
                <strong>Choisir un transporteur à Paris</strong> n'est pas une décision à prendre à la légère. Que vous soyez une entreprise cherchant un partenaire logistique régulier ou un particulier ayant besoin d'un service ponctuel, les critères de sélection sont nombreux. Dans ce guide complet, nous vous dévoilons tous les secrets pour identifier le <strong>meilleur transporteur à Paris</strong> selon vos besoins spécifiques. Fiabilité, rapidité, tarifs, services : découvrez notre <strong>comparatif transporteur</strong> détaillé et nos conseils d'experts.
              </p>
            </div>

            {/* Section 1 */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <a href="#pourquoi-important" id="pourquoi-important" className="hover:text-teal-600 transition-colors">
                  Pourquoi bien choisir son transporteur est crucial ?
                </a>
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Le choix d'un <strong>transporteur professionnel à Paris</strong> impacte directement plusieurs aspects de votre activité ou de votre vie quotidienne :
              </p>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-money-euro-circle-line text-2xl text-teal-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Impact financier</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Un mauvais choix peut coûter cher : retards, colis perdus, frais cachés. Un bon transporteur optimise vos coûts logistiques et évite les mauvaises surprises.
                  </p>
                </div>

                <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-customer-service-2-line text-2xl text-teal-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Satisfaction client</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Pour les entreprises, la qualité du transport reflète votre image de marque. Des livraisons ponctuelles et soignées fidélisent vos clients.
                  </p>
                </div>

                <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-time-line text-2xl text-teal-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Gain de temps</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Un transporteur fiable vous libère du stress logistique. Vous pouvez vous concentrer sur votre cœur de métier en toute sérénité.
                  </p>
                </div>

                <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-shield-check-line text-2xl text-teal-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Sécurité des envois</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Marchandises fragiles, documents confidentiels : un transporteur professionnel garantit la sécurité et l'intégrité de vos envois.
                  </p>
                </div>
              </div>

              <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                  <i className="ri-error-warning-line text-amber-600 mr-2"></i>
                  Attention aux pièges !
                </h3>
                <p className="text-gray-700">
                  Selon une étude récente, 42% des entreprises parisiennes ont déjà changé de transporteur en raison de problèmes récurrents (retards, colis endommagés, service client défaillant). Prendre le temps de bien choisir dès le départ vous évitera ces désagréments.
                </p>
              </div>
            </section>

            {/* Section 2 */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <a href="#criteres-essentiels" id="criteres-essentiels" className="hover:text-teal-600 transition-colors">
                  Les 10 critères essentiels pour choisir son transporteur
                </a>
              </h2>

              <div className="space-y-8">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-xl mr-6 flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Zone de couverture géographique</h3>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      Vérifiez que le transporteur couvre bien vos zones de livraison. À Paris, certains se limitent à l'intra-muros, d'autres couvrent toute l'Île-de-France. Speed Transport, par exemple, assure des <Link to="/transport-paris" className="text-teal-600 font-semibold hover:text-teal-700">livraisons dans tout Paris et sa région</Link>.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      <strong>Questions à poser :</strong> Livrez-vous en banlieue ? Quels sont vos délais pour la petite/grande couronne ? Y a-t-il des suppléments selon les zones ? Consultez notre <Link to="/#faq" className="text-teal-600 font-semibold hover:text-teal-700">FAQ pour plus de réponses</Link>.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-xl mr-6 flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Types de services proposés</h3>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      Identifiez vos besoins : <Link to="/livraison-express-paris" className="text-teal-600 font-semibold hover:text-teal-700">livraison express</Link>, transport standard, <Link to="/coursier-paris" className="text-teal-600 font-semibold hover:text-teal-700">coursier moto</Link>, <Link to="/demenagement-paris" className="text-teal-600 font-semibold hover:text-teal-700">déménagement</Link>, transport de marchandises volumineuses...
                    </p>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-700">
                        <strong>💡 Conseil :</strong> Privilégiez un transporteur polyvalent qui pourra s'adapter à l'évolution de vos besoins plutôt que de multiplier les prestataires. Lisez aussi notre article sur la <Link to="/blog/livraison-dernier-kilometre-paris" className="text-teal-600 font-semibold hover:text-teal-700">livraison dernier kilomètre</Link>.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-xl mr-6 flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Délais de livraison garantis</h3>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      Les délais annoncés sont-ils réalistes ? Sont-ils garantis par écrit ? Quelle est la politique en cas de retard ?
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Un <strong>transporteur sérieux à Paris</strong> vous communiquera des délais précis et s'engagera contractuellement. Méfiez-vous des promesses trop belles pour être vraies.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-xl mr-6 flex-shrink-0">
                    4
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Disponibilité et flexibilité</h3>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      Horaires d'ouverture, disponibilité le week-end, service d'urgence : ces éléments sont cruciaux. Avez-vous besoin d'un <Link to="/blog/transport-urgent-weekend-paris" className="text-teal-600 font-semibold hover:text-teal-700">transport urgent le week-end</Link> ?
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Speed Transport propose un service 24h/24, 7j/7, idéal pour les urgences et les besoins imprévisibles. Toutes les réponses dans notre <Link to="/#faq" className="text-teal-600 font-semibold hover:text-teal-700">FAQ coursier Île-de-France</Link>.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-xl mr-6 flex-shrink-0">
                    5
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Traçabilité et suivi en temps réel</h3>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      À l'ère du digital, le suivi GPS en temps réel est devenu indispensable. Vous devez pouvoir localiser votre colis à tout moment et recevoir des notifications automatiques.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      <strong>Fonctionnalités à vérifier :</strong> Application mobile, SMS de suivi, interface web, preuve de livraison électronique avec signature.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-xl mr-6 flex-shrink-0">
                    6
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Tarification transparente</h3>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      Exigez un devis détaillé et transparent. Attention aux frais cachés : suppléments carburant, frais de zone, majorations week-end non annoncées...
                    </p>
                    <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                      <p className="text-sm text-gray-700">
                        <strong>✅ Bon signe :</strong> Un transporteur professionnel vous fournit un devis écrit avec tous les détails : tarif de base, suppléments éventuels, conditions d'annulation, modalités de paiement.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-xl mr-6 flex-shrink-0">
                    7
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Assurance et responsabilité</h3>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      Vérifiez les garanties d'assurance : quelle est la couverture en cas de perte, vol ou dommage ? Quelles sont les exclusions ? Quel est le plafond d'indemnisation ?
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Pour des envois de valeur, demandez une assurance complémentaire. Un <strong>transporteur fiable à Paris</strong> dispose d'une assurance professionnelle solide.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-xl mr-6 flex-shrink-0">
                    8
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Qualité du service client</h3>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      Testez la réactivité du service client avant de vous engager. Appelez-les, posez des questions, observez leur professionnalisme et leur disponibilité.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      <strong>Points à évaluer :</strong> Temps de réponse, amabilité, compétence des conseillers, disponibilité multicanale (téléphone, email, chat).
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-xl mr-6 flex-shrink-0">
                    9
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Réputation et avis clients</h3>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      Consultez les avis en ligne (Google, Trustpilot, réseaux sociaux). Attention aux avis trop parfaits ou trop négatifs : cherchez la cohérence et les retours détaillés.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Demandez également des références clients, surtout pour des contrats réguliers. Un bon transporteur sera fier de vous mettre en contact avec ses clients satisfaits.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-xl mr-6 flex-shrink-0">
                    10
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Engagement écologique</h3>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      Avec les restrictions ZFE à Paris, l'engagement écologique devient un critère majeur. Quelle est la composition de la flotte ? Véhicules électriques ? Hybrides ?
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Au-delà de la conformité réglementaire, un transporteur éco-responsable optimise ses tournées pour réduire son empreinte carbone. C'est bon pour la planète et pour vos coûts !
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Checklist */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <a href="#checklist" id="checklist" className="hover:text-teal-600 transition-colors">
                  Checklist : Questions à poser avant de choisir
                </a>
              </h2>

              <div className="bg-gradient-to-r from-teal-50 to-emerald-50 border-2 border-teal-200 rounded-xl p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                  Votre guide de sélection en 15 questions
                </h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-start">
                      <i className="ri-checkbox-blank-circle-line text-teal-600 text-xl mr-3 mt-1 flex-shrink-0"></i>
                      <p className="text-gray-700">Quelles sont vos zones de livraison exactes ?</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-start">
                      <i className="ri-checkbox-blank-circle-line text-teal-600 text-xl mr-3 mt-1 flex-shrink-0"></i>
                      <p className="text-gray-700">Quels sont vos délais de livraison garantis ?</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-start">
                      <i className="ri-checkbox-blank-circle-line text-teal-600 text-xl mr-3 mt-1 flex-shrink-0"></i>
                      <p className="text-gray-700">Êtes-vous disponibles le week-end et jours fériés ?</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-start">
                      <i className="ri-checkbox-blank-circle-line text-teal-600 text-xl mr-3 mt-1 flex-shrink-0"></i>
                      <p className="text-gray-700">Proposez-vous un suivi GPS en temps réel ?</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-start">
                      <i className="ri-checkbox-blank-circle-line text-teal-600 text-xl mr-3 mt-1 flex-shrink-0"></i>
                      <p className="text-gray-700">Quelle est votre politique tarifaire (frais cachés) ?</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-start">
                      <i className="ri-checkbox-blank-circle-line text-teal-600 text-xl mr-3 mt-1 flex-shrink-0"></i>
                      <p className="text-gray-700">Quelle assurance couvre mes envois ?</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-start">
                      <i className="ri-checkbox-blank-circle-line text-teal-600 text-xl mr-3 mt-1 flex-shrink-0"></i>
                      <p className="text-gray-700">Quel est le plafond d'indemnisation ?</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-start">
                      <i className="ri-checkbox-blank-circle-line text-teal-600 text-xl mr-3 mt-1 flex-shrink-0"></i>
                      <p className="text-gray-700">Comment gérez-vous les urgences et imprévus ?</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-start">
                      <i className="ri-checkbox-blank-circle-line text-teal-600 text-xl mr-3 mt-1 flex-shrink-0"></i>
                      <p className="text-gray-700">Quels types de véhicules composent votre flotte ?</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-start">
                      <i className="ri-checkbox-blank-circle-line text-teal-600 text-xl mr-3 mt-1 flex-shrink-0"></i>
                      <p className="text-gray-700">Avez-vous des véhicules électriques/hybrides ?</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-start">
                      <i className="ri-checkbox-blank-circle-line text-teal-600 text-xl mr-3 mt-1 flex-shrink-0"></i>
                      <p className="text-gray-700">Proposez-vous des contrats réguliers avec tarifs dégressifs ?</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-start">
                      <i className="ri-checkbox-blank-circle-line text-teal-600 text-xl mr-3 mt-1 flex-shrink-0"></i>
                      <p className="text-gray-700">Quelle est votre politique d'annulation ?</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-start">
                      <i className="ri-checkbox-blank-circle-line text-teal-600 text-xl mr-3 mt-1 flex-shrink-0"></i>
                      <p className="text-gray-700">Comment puis-je contacter le service client ?</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-start">
                      <i className="ri-checkbox-blank-circle-line text-teal-600 text-xl mr-3 mt-1 flex-shrink-0"></i>
                      <p className="text-gray-700">Pouvez-vous me fournir des références clients ?</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-start">
                      <i className="ri-checkbox-blank-circle-line text-teal-600 text-xl mr-3 mt-1 flex-shrink-0"></i>
                      <p className="text-gray-700">Quelles sont vos certifications professionnelles ?</p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-white rounded-lg text-center">
                  <p className="text-gray-700">
                    <strong>📋 Conseil :</strong> Imprimez cette checklist et cochez les réponses lors de vos échanges avec les transporteurs. Cela vous aidera à comparer objectivement les offres.
                  </p>
                </div>
              </div>
            </section>

            {/* Section 3 */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <a href="#erreurs-eviter" id="erreurs-eviter" className="hover:text-teal-600 transition-colors">
                  Les 5 erreurs à éviter absolument
                </a>
              </h2>

              <div className="space-y-6">
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                    <i className="ri-close-circle-line text-red-600 mr-2"></i>
                    Erreur n°1 : Choisir uniquement sur le prix
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Le transporteur le moins cher n'est pas toujours le plus économique à long terme. Retards, colis perdus, service client inexistant : ces problèmes coûtent bien plus cher qu'une différence de tarif initiale. Privilégiez le rapport qualité-prix.
                  </p>
                </div>

                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                    <i className="ri-close-circle-line text-red-600 mr-2"></i>
                    Erreur n°2 : Ne pas vérifier les assurances
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Beaucoup découvrent trop tard que leur colis de valeur n'était couvert qu'à hauteur de 50€. Lisez attentivement les conditions d'assurance et souscrivez une couverture adaptée à vos envois.
                  </p>
                </div>

                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                    <i className="ri-close-circle-line text-red-600 mr-2"></i>
                    Erreur n°3 : Ignorer les avis clients
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Les avis en ligne sont une mine d'informations. Des retours négatifs récurrents sur les mêmes points (retards, colis endommagés, service client) doivent vous alerter. Prenez le temps de les consulter.
                  </p>
                </div>

                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                    <i className="ri-close-circle-line text-red-600 mr-2"></i>
                    Erreur n°4 : Ne pas tester le service client
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Appelez le transporteur avant de vous engager. Si vous n'arrivez pas à joindre quelqu'un ou si les réponses sont évasives, imaginez la situation en cas de problème avec votre livraison...
                  </p>
                </div>

                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                    <i className="ri-close-circle-line text-red-600 mr-2"></i>
                    Erreur n°5 : S'engager sans contrat écrit
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Même pour une course ponctuelle, exigez un devis écrit avec les conditions détaillées. Un accord verbal n'a aucune valeur en cas de litige. Protégez-vous avec un document signé.
                  </p>
                </div>
              </div>
            </section>

            {/* Section 4 */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <a href="#pourquoi-speed-transport" id="pourquoi-speed-transport" className="hover:text-teal-600 transition-colors">
                  Pourquoi Speed Transport est le choix idéal à Paris ?
                </a>
              </h2>

              <p className="text-gray-700 leading-relaxed mb-8">
                Après avoir lu ce guide, vous comprenez les critères d'un <strong>excellent transporteur à Paris</strong>. Voici comment Speed Transport répond à chacun de ces critères :
              </p>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-white border-2 border-teal-200 rounded-lg p-6 hover:border-teal-500 transition-colors">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4">
                      <i className="ri-map-pin-range-line text-2xl text-teal-600"></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Couverture complète</h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    Paris intra-muros, petite et grande couronne, toute l'Île-de-France. Nous livrons partout où vous en avez besoin.
                  </p>
                </div>

                <div className="bg-white border-2 border-teal-200 rounded-lg p-6 hover:border-teal-500 transition-colors">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4">
                      <i className="ri-24-hours-line text-2xl text-teal-600"></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Service 24/7</h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    Disponibles jour et nuit, week-ends et jours fériés. Vos urgences sont nos priorités.
                  </p>
                </div>

                <div className="bg-white border-2 border-teal-200 rounded-lg p-6 hover:border-teal-500 transition-colors">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4">
                      <i className="ri-flashlight-line text-2xl text-teal-600"></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Rapidité garantie</h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    Prise en charge express sous 30 minutes, livraison en 1h pour les urgences. Délais respectés ou remboursés.
                  </p>
                </div>

                <div className="bg-white border-2 border-teal-200 rounded-lg p-6 hover:border-teal-500 transition-colors">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4">
                      <i className="ri-gps-line text-2xl text-teal-600"></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Suivi temps réel</h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    Géolocalisation GPS, notifications automatiques, preuve de livraison électronique. Transparence totale.
                  </p>
                </div>

                <div className="bg-white border-2 border-teal-200 rounded-lg p-6 hover:border-teal-500 transition-colors">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4">
                      <i className="ri-price-tag-3-line text-2xl text-teal-600"></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Tarifs transparents</h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    Devis gratuit et détaillé, aucun frais caché. Tarifs dégressifs pour les clients réguliers.
                  </p>
                </div>

                <div className="bg-white border-2 border-teal-200 rounded-lg p-6 hover:border-teal-500 transition-colors">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4">
                      <i className="ri-leaf-line text-2xl text-teal-600"></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Flotte écologique</h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    Véhicules électriques et hybrides, conformes aux normes ZFE. Transport responsable et durable.
                  </p>
                </div>

                <div className="bg-white border-2 border-teal-200 rounded-lg p-6 hover:border-teal-500 transition-colors">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4">
                      <i className="ri-shield-check-line text-2xl text-teal-600"></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Assurance complète</h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    Tous vos envois sont assurés. Possibilité d'assurance complémentaire pour les marchandises de valeur.
                  </p>
                </div>

                <div className="bg-white border-2 border-teal-200 rounded-lg p-6 hover:border-teal-500 transition-colors">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4">
                      <i className="ri-customer-service-2-line text-2xl text-teal-600"></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Service client réactif</h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    Équipe disponible et à l'écoute. Réponse rapide à toutes vos questions. Satisfaction client : notre priorité.
                  </p>
                </div>
              </div>

              <div className="bg-gradient-to-r from-teal-500 to-emerald-600 rounded-xl p-8 text-white text-center">
                <h3 className="text-2xl font-bold mb-4">
                  Plus de 5 000 clients nous font confiance à Paris
                </h3>
                <p className="text-xl text-white/90 mb-6">
                  Entreprises, commerces, particuliers : rejoignez nos clients satisfaits et bénéficiez d'un service de transport professionnel et fiable.
                </p>
                <div className="flex items-center justify-center space-x-8 text-white">
                  <div>
                    <div className="text-4xl font-bold">98%</div>
                    <div className="text-sm text-white/80">Satisfaction client</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold">24/7</div>
                    <div className="text-sm text-white/80">Disponibilité</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold">&lt;30min</div>
                    <div className="text-sm text-white/80">Prise en charge express</div>
                  </div>
                </div>
              </div>
            </section>

            {/* CTA Section */}
            <section className="bg-gradient-to-r from-teal-500 to-emerald-600 rounded-xl p-8 md:p-12 text-white text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">
                Prêt à choisir le meilleur transporteur à Paris ?
              </h2>
              <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                <strong>Speed Transport</strong> vous accompagne dans tous vos projets de transport à Paris et en Île-de-France. 
                Contactez-nous au <a href="tel:+33632410246" className="text-orange-600 hover:text-orange-700 font-semibold">+33 6 32 41 02 46</a> pour 
                un devis personnalisé et des conseils d'experts.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="tel:+33632410246"
                  className="inline-flex items-center justify-center bg-white text-teal-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-50 transition-colors shadow-lg whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line mr-2"></i>
                  +33 6 32 41 02 46
                </a>
                <Link
                  to="/transport-paris"
                  className="inline-flex items-center justify-center bg-teal-700 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-teal-800 transition-colors shadow-lg whitespace-nowrap cursor-pointer"
                >
                  Découvrir nos services
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
              </div>
            </section>

            {/* Price Simulator */}
            <section className="mb-12">
              <PriceSimulator />
            </section>

            {/* Related Articles */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Articles connexes</h2>
              <div className="grid md:grid-cols-3 gap-6">
                {relatedArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/blog/${article.slug}`}
                    className="group bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300"
                  >
                    <div className="relative w-full h-48">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-lg font-bold text-gray-900 group-hover:text-teal-600 transition-colors">
                        {article.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          </div>
        </article>
      </div>

      <Footer />
    </>
  );
}
